import {
  PUSH_NOTIFICATION_ACTION_LABELS,
  PushNotificationAction,
  PushNotificationType,
  WebPushDataKey,
  getMeetingPlatformLabel,
  normalizeMeetingLink,
  parsePushNotificationActions,
  type PushNotificationPayload,
} from "@speakai/shared";
import { cleanDisplayText } from "@speakai/common/src/utils/format";

export const EVENT_ID_MAX_LENGTH = 128;
export const TITLE_MAX_LENGTH = 120;
export const BODY_MAX_LENGTH = 120;
const MS_PER_MINUTE = 60_000;
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;
const SUPPORTED_ACTIONS: readonly PushNotificationAction[] = [PushNotificationAction.JOIN];

export const REMINDER_MESSAGE_SHOW = "meeting-reminder:show";
export const REMINDER_DASHBOARD_PATH = "/home";

type ReminderData = Partial<Record<keyof PushNotificationPayload, unknown>>;

export interface MeetingReminder {
  eventId: string;
  tag: string;
  title: string;
  body: string;
  startTime: string;
  platform: string;
  meetingUrl: string | null;
  actions: PushNotificationAction[];
}

interface NotificationActionButton {
  action: PushNotificationAction;
  title: string;
}

export type ReminderClickTarget = { kind: "external"; url: string } | { kind: "app"; path: string } | { kind: "page"; path: string };

export function cleanText(value: unknown, maxLength: number): string {
  return typeof value === "string" ? cleanDisplayText(value, maxLength) : "";
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function extractPushData(payload: unknown): Record<string, unknown> | null {
  if (!isRecord(payload)) return null;
  return isRecord(payload.data) ? payload.data : payload;
}

export function parseMeetingReminder(raw: unknown): MeetingReminder | null {
  if (!isRecord(raw) || raw.type !== PushNotificationType.MEETING_REMINDER) return null;
  const data: ReminderData = raw;
  const webValue = (key: WebPushDataKey): unknown => raw[key];
  const eventId = typeof data.eventId === "string" ? data.eventId.trim() : "";
  const startTime = typeof data.startTime === "string" ? data.startTime.trim() : "";
  const platformLabel = getMeetingPlatformLabel(data.platform);
  if (!eventId || eventId.length > EVENT_ID_MAX_LENGTH) return null;
  if (!ISO_TIMESTAMP.test(startTime) || Number.isNaN(Date.parse(startTime))) return null;
  if (!platformLabel) return null;
  const meetingUrl = normalizeMeetingLink(typeof data.meetingURL === "string" ? data.meetingURL : null);
  const requested = parsePushNotificationActions(typeof data.actions === "string" ? data.actions : null);
  const actions = SUPPORTED_ACTIONS.filter(
    (action) => requested.includes(action) && (action !== PushNotificationAction.JOIN || meetingUrl !== null),
  );
  const tag = cleanText(webValue(WebPushDataKey.TAG), EVENT_ID_MAX_LENGTH);
  return {
    eventId,
    tag: tag || eventId,
    title: cleanText(webValue(WebPushDataKey.TITLE), TITLE_MAX_LENGTH) || platformLabel,
    body: cleanText(webValue(WebPushDataKey.MESSAGE), BODY_MAX_LENGTH),
    startTime,
    platform: data.platform as string,
    meetingUrl,
    actions,
  };
}

export function buildNotificationActions(reminder: Pick<MeetingReminder, "actions">, maxActions: number): NotificationActionButton[] {
  return reminder.actions
    .slice(0, Math.max(0, maxActions))
    .map((action) => ({ action, title: PUSH_NOTIFICATION_ACTION_LABELS[action] }));
}

export function resolveClickTarget(reminder: MeetingReminder, action: string): ReminderClickTarget | null {
  const isBodyClick = action === "";
  if ((isBodyClick || action === PushNotificationAction.JOIN) && reminder.meetingUrl) {
    return { kind: "external", url: reminder.meetingUrl };
  }
  return isBodyClick ? { kind: "app", path: REMINDER_DASHBOARD_PATH } : null;
}

export function getMinutesUntilStart(startTime: string, now: number): number {
  return Math.max(0, Math.ceil((Date.parse(startTime) - now) / MS_PER_MINUTE));
}
